import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Bars3Icon, XMarkIcon, ChevronDownIcon, ArrowUpRightIcon } from '@heroicons/react/24/outline';

// Resume Data (Chinese and English)
const resumeData = {
  zh: {
    name: '成啸诚',
    title: '技术专家 / 产品负责人',
    tagline: '专注于智能硬件产品商业化落地，拥有16年丰富的技术架构、产品规划及团队管理经验',
    contact: {
      email: 'greatcxc@gmail.com',
      phone: '+86 135 6415 7153',
      website: 'http://ai.greatcxc.cn',
      websiteLabel: 'Digital Twin'
    },
    summary: [
      '16年智能硬件产品商业化经验，专注于从0到1的产品开发与市场推广。',
      '在技术架构、产品规划、团队管理、海外电商运营及复杂项目实施方面积累深厚。',
      '成功主导10+智能硬件产品商业化，涵盖触摸一体机、AIoT设备及智能零售终端，实现年营收超300万美元。'
    ],
    skills: [
      {
        category: '产品与技术',
        items: [
          '技术架构设计与优化',
          '产品生命周期管理',
          '智能硬件产品商业化',
          '系统集成与性能优化'
        ]
      },
      {
        category: '团队与项目管理',
        items: [
          '跨职能团队协作',
          '敏捷开发方法论',
          '复杂项目落地执行',
          '资源协调与进度控制'
        ]
      },
      {
        category: '市场与运营',
        items: [
          '海外市场拓展',
          'B2B/B2C电商平台运营',
          '品牌营销策略制定',
          '客户关系管理'
        ]
      }
    ],
    experience: [
      {
        position: '产品市场总监',
        company: '上海品奇科技有限公司',
        period: '2009年 - 至今',
        description: '公司简介：专注于智能硬件与AI技术整合，产品涵盖触摸屏、AI物体识别设备、3D数字人系统等，客户包括LG、HP等跨国企业。',
        responsibilities: {
          summary: '16年智能硬件商业化经验，主导10+产品从0到1落地，实现300万美元年营收',
          productManagement: {
            title: '产品管理与技术整合',
            period: '2016年 - 至今',
            responsibilities: [
              '主导AI物体识别触摸桌、3D数字人快速生成系统等创新产品的研发与商业化落地',
              '推动AI技术与硬件产品结合，如低成本边缘AI加速、物体识别交互技术',
              '负责产品全生命周期管理，从需求分析、技术方案设计到供应链管控与市场推广'
            ],
            achievements: [
              'AI物体识别触摸桌年销售额近千万元，获数十家媒体报道',
              '3D数字人系统成为业内首个秒级生成超写实数字人的技术方案，应用于虚拟人直播、元宇宙会展等领域'
            ]
          },
          crossBorderBusiness: {
            title: '跨境业务与市场拓展',
            period: '2015年 - 2020年',
            responsibilities: [
              '搭建海外电商平台（Shopify+Amazon），主导Kickstarter众筹项目，开拓欧美市场',
              '负责海外仓储与本地化服务体系，优化国际物流与支付结算流程',
              '主导LG、HP等跨国客户项目，推动触摸屏产品进入全球市场'
            ],
            achievements: [
              '海外电商年营收超300万美元，沉淀1000+企业客户资源',
              'Kickstarter项目预热热度位列科技类前三，硬件类第一，吸引690+支持者'
            ]
          },
          operations: {
            title: '运营与体系建设',
            period: '2012年 - 2016年',
            responsibilities: [
              '牵头部署ERP系统（基于Odoo），优化生产与采购计划，提升运营效率',
              '主导ISO9001质量管理体系认证，推动产品不良率下降20%',
              '负责公司触摸组件产品迭代，年营收达数千万美元'
            ],
            achievements: [
              '通过ERP系统部署提升运营效率，优化供应链管理',
              '成功获得ISO9001认证，产品质量显著提升'
            ]
          },
          earlyExperience: {
            title: '早期经历：市场与项目管理',
            period: '2009年 - 2012年',
            responsibilities: [
              '负责网站搭建与在线推广，擅长SEO与EDM营销',
              '协调内部生产、研发、质检等部门，推进LG、HP等OEM项目交付'
            ],
            achievements: [
              '成功搭建公司在线营销体系，提升品牌知名度',
              '确保多个跨国OEM项目按时高质量交付'
            ]
          }
        }
      }
    ],
    projects: [
      {
        title: '大模型驱动的销售模拟培训系统',
        period: '2024/11 - 2025/02',
        description: '帮助药企销售代表进行话术培训和测试的数字人辅助系统。核心功能包括：\n1. 通过大模型来模拟医生角色，让销售代表尝试向医生介绍产品，并说服大模型医生（本地部署大模型+RAG+Prompt工程）；\n2. 通过医生形象的3D数字人和销售代表进行实时语音对话沟通（数字人+实时TTS/语音识别）；\n3. 系统自动对销售代表的表现进行多维度评估和打分，并提供改进建议；\n4. 支持多终端（大屏幕/笔记本/手机）；',
        achievements: '项目在3个月内顺利完工，获得客户的认可。目前已经并入客户的销售培训流程，并在其全国20+城市Office部署。'
      },
      {
        title: '大模型驱动的小学教学视频点播系统',
        period: '2024/08 - 2024/12',
        description: '小学教学设备，通过大模型理解小学生不准确的语音指令，并在数百个教学视频中准确检索视频，并在前台设备进行播放。主要难点：低年级小学生的语音指令面临发音不清晰、文字不准确等难以被简单的语音识别功能准确识别的情况。\n\n系统方案：在语音识别之后加一道大模型处理流程，使用专门微调的大模型来理解语音识别的不准确内容，并输出正确的目标视频ID，在前台进行播放；\n\n大模型解决方案：结合教学视频库内容，整理常见错误语音识别情况，生成纠错语料库。基于此对大模型进行微调训练（最后选择qwen2.0 7b模型本地部署），大大提高了小学生的语音点播准确率。',
        achievements: '项目经过三个月研发，与客户硬件设备整合完毕。在客户测试教学场景完整部署并测试交付，实现了小学生语音视频点播98.5%的成功率，远超客户90%的项目指标要求。'
      },
      {
        title: '3D数字人快速生成系统',
        period: '2023/05 - 至今',
        description: '通过单张RGB照片生成完整3D数字人形象（面部、表情驱动、服装适配），支持UE/Unity引擎及Web 3D渲染。\n\n职责：\n- 调研并整合多项技术方案（AI生成模型、AI生成贴图、Blendshape迁移、面部表情驱动、动捕骨骼驱动等）；\n- 协调不同技术团队的任务节点计划，确定技术方案间的接口通信；\n- 主导测试流程，确保产品稳定性和用户体验。\n\n业绩：\n- 实现业内首个秒级超写实数字人生成方案；\n- 测试用户覆盖虚拟人直播、会展等地域，技术方案获多家企业客户采用。',
        achievements: '实现业内首个秒级超写实数字人生成方案。测试用户覆盖虚拟人直播、会展等地域，技术方案获多家企业客户采用。'
      },
      {
        title: 'AI物体识别触摸桌',
        period: '2019/03 – 2021',
        description: '整合AI物体识别技术的触摸桌产品，可识别桌面物体并通过触摸交互展示内容。\n\n职责：\n- 设计硬件架构（NUC计算单元+隐藏摄像头），优化AI推理性能；\n- 制定GTM策略，通过会展、信息流广告推广产品。\n\n业绩：\n- 2019年重庆智博会首发售出近百台，年销售额近千万元；\n- 获36氪、极客公园等媒体报道，技术方案被多家Intel等多家知名企业复购。',
        achievements: '2019年重庆智博会首发售出上百台，年销售额近千万元。获36氪、极客公园等媒体报道，技术方案被多家Intel等多家知名企业复购。'
      },
      {
        title: '海外众筹项目（家用智能触摸桌）',
        period: '2021/01 - 2021/05',
        description: '通过Kickstarter众筹推出2C智能触摸桌，主打家庭Party场景（物体识别、无线充电）。\n\n职责：\n- 定义产品功能与硬件架构（Amlogic S905芯片+自研AI加速技术）；\n- 策划预热活动（Youtube/Facebook广告），研究平台规则。\n\n业绩：\n- 项目位列科技类前三，硬件类第一，吸引690+支持者；\n- 因疫情终止，但沉淀完整众筹方法论与用户社群资源。',
        achievements: '项目位列科技类前三，硬件类第一，吸引690+支持者。因疫情终止，但沉淀完整众筹方法论与用户社群资源。'
      },
      {
        title: '户外AI识别触摸橱窗',
        period: '2018/04 – 2020/04',
        description: '通过AI识别技术实现户外橱窗触摸交互，无需户外走线或改动店铺装修。\n\n职责：\n- 提出产品概念并主导设计，协调研发团队实现AI识别与触摸交互功能；\n- 负责产品上市后的经销商培训与技术支持。\n\n业绩：\n- 产品被天猫、耐克（美国）、三星（欧洲）等多个知名品牌门店采用；\n- 技术方案提升店铺互动体验，获得客户高度认可。',
        achievements: '产品被天猫、耐克（美国）、三星（欧洲）等多个知名品牌门店采用；\n- 技术方案提升店铺互动体验，获得客户高度认可。'
      },
      {
        title: '触摸一体机海外电商项目',
        period: '2015/01 - 2020/05',
        description: '通过独立站电商（Magento/Shopify）销售触摸一体机产品，建立公司硬件产品的电商销售渠道。\n\n职责：\n- 提出产品概念并主导设计，协调研发团队实现AI识别与触摸交互功能；\n- 负责产品上市后的经销商培训与技术支持。\n\n业绩：\n- 产品被天猫、耐克（美国）、三星（欧洲）等多个知名品牌门店采用；\n- 技术方案提升店铺互动体验，获得客户高度认可。',
        achievements: '产品被天猫、耐克（美国）、三星（欧洲）等多个知名品牌门店采用；\n- 技术方案提升店铺互动体验，获得客户高度认可。'
      }
    ],
    education: [
      { school: '上海海事大学', degree: '国际法学硕士', period: '2007 - 2009' },
      { school: '兰州财经大学', degree: '国际经济与贸易学士', period: '2002 - 2006' }
    ]
  },
  en: {
    name: 'Cheng Xiao Cheng',
    title: 'Technical Expert / Product Owner',
    tagline: 'Focused on commercialization of smart hardware products with 16 years of extensive experience in technical architecture, product planning, and team management',
    contact: {
      email: 'greatcxc@gmail.com',
      phone: '+86 135 6415 7153',
      website: 'http://ai.greatcxc.cn',
      websiteLabel: 'Digital Twin'
    },
    summary: [
      '16 years of extensive experience in commercializing smart hardware products, specializing in product development and market promotion from 0 to 1.',
      'Deep accumulation in technical architecture, product planning, team management, overseas e-commerce operations, and complex project implementation.',
      'Successfully led the commercialization of 10+ smart hardware products, covering touch all-in-one machines, AIoT devices, and smart retail terminals, achieving annual revenue of over 3 million US dollars.'
    ],
    skills: [
      {
        category: 'Core Skills',
        items: [
          'Technical Architecture Design & Optimization',
          'Product Lifecycle Management',
          'Smart Hardware Product Commercialization',
          'System Integration & Performance Optimization'
        ]
      },
      {
        category: 'Work Experience',
        items: [
          'Cross-functional Team Collaboration',
          'Agile Development Methodology',
          'Complex Project Implementation',
          'Resource Coordination & Schedule Control'
        ]
      },
      {
        category: 'Marketing & Operations',
        items: [
          'Overseas Market Expansion',
          'B2B/B2C E-commerce Platform Operations',
          'Brand Marketing Strategy Development',
          'Customer Relationship Management'
        ]
      }
    ],
    experience: [
      {
        position: 'Product Marketing Director',
        company: 'PQ Labs, Inc.',
        period: '2009 - Present',
        description: 'Company Profile: Focused on smart hardware and AI technology integration, with products covering touchscreens, AI object recognition devices, and 3D digital human systems. Clients include multinational enterprises such as LG and HP.',
        responsibilities: {
          summary: '16 years of smart hardware commercialization experience, leading 10+ products from 0 to 1 with $3M+ annual revenue',
          productManagement: {
            title: 'Product Management & Technical Integration',
            period: '2016 - Present',
            responsibilities: [
              'Led development and commercialization of innovative products including AI object recognition touch tables and 3D digital human rapid generation systems',
              'Drove integration of AI technology with hardware products, such as low-cost edge AI acceleration and object recognition interaction technology',
              'Responsible for full product lifecycle management from requirement analysis and technical solution design to supply chain control and market promotion'
            ],
            achievements: [
              'AI object recognition touch table achieved annual sales of nearly 10 million RMB with coverage by dozens of media outlets',
              '3D digital human system became the industry\'s first technology solution for second-level ultra-realistic digital human generation, applied in virtual human live streaming and metaverse exhibitions'
            ]
          },
          crossBorderBusiness: {
            title: 'Cross-border Business & Market Expansion',
            period: '2015 - 2020',
            responsibilities: [
              'Built overseas e-commerce platforms (Shopify+Amazon), led Kickstarter crowdfunding projects, and expanded into European and American markets',
              'Responsible for overseas warehousing and localized service systems, optimizing international logistics and payment settlement processes',
              'Led multinational client projects including LG and HP, driving touchscreen products into global markets'
            ],
            achievements: [
              'Overseas e-commerce achieved annual revenue exceeding 3 million USD, accumulating 1000+ enterprise client resources',
              'Kickstarter project ranked top 3 in technology category and #1 in hardware category, attracting 690+ backers'
            ]
          },
          operations: {
            title: 'Operations & System Development',
            period: '2012 - 2016',
            responsibilities: [
              'Led deployment of ERP system (based on Odoo), optimizing production and procurement planning to improve operational efficiency',
              'Led ISO9001 quality management system certification, driving 20% reduction in product defect rates',
              'Responsible for company touchscreen component product iteration with annual revenue reaching tens of millions of USD'
            ],
            achievements: [
              'Improved operational efficiency and optimized supply chain management through ERP system deployment',
              'Successfully obtained ISO9001 certification with significant product quality improvements'
            ]
          },
          earlyExperience: {
            title: 'Early Career: Marketing & Project Management',
            period: '2009 - 2012',
            responsibilities: [
              'Responsible for website development and online promotion, specializing in SEO and EDM marketing',
              'Coordinated internal production, R&D, and quality inspection departments to advance LG, HP and other OEM project deliveries'
            ],
            achievements: [
              'Successfully established company online marketing system to enhance brand awareness',
              'Ensured timely and high-quality delivery of multiple multinational OEM projects'
            ]
          }
        }
      }
    ],
    projects: [
      {
        title: 'Large Model-Driven Sales Simulation Training System',
        period: '2024/11 - 2025/02',
        description: 'A digital human-assisted system designed to help pharmaceutical sales representatives practice and test their sales pitches. Core features include:\n1. Using large models to simulate doctor roles, allowing sales representatives to introduce products to the large model doctor (locally deployed large model + RAG + Prompt engineering);\n2. Real-time voice dialogue communication between the doctor\'s 3D digital human and the sales representative (digital human + real-time TTS/speech recognition);\n3. The system automatically evaluates and scores the sales representative\'s performance from multiple dimensions and provides improvement suggestions;\n4. Support for multiple terminals (large screen/notebook/phone);',
        achievements: 'The project was successfully completed within 3 months and received client recognition. It has now been integrated into the client\'s sales training process and deployed in over 20 cities nationwide.'
      },
      {
        title: 'Large Model-Driven Elementary School Teaching Video On-Demand System',
        period: '2024/08 - 2024/12',
        description: 'An elementary school teaching device that uses large models to understand inaccurate voice commands from young students and accurately retrieve videos from hundreds of teaching videos for playback on the front-end device. Main challenges: Voice commands from lower-grade students face difficulties such as unclear pronunciation and inaccurate text, which are hard to accurately recognize with simple speech recognition functions.\n\nSystem solution: Add a large model processing step after speech recognition, using a specially fine-tuned large model to understand the inaccurate content of speech recognition and output the correct target video ID for playback on the front-end;\n\nLarge model solution: Combine the teaching video library content, organize common speech recognition error situations, and generate a correction corpus. Based on this, fine-tune the large model training (finally choosing the qwen2.0 7b model for local deployment), greatly improving the accuracy rate of student voice-on-demand.',
        achievements: 'After three months of research and development, the project was integrated with the client\'s hardware equipment. It was fully deployed and tested in the client\'s teaching scenario, achieving a 98.5% success rate for student voice video-on-demand, far exceeding the client\'s project indicator requirement of 90%.'
      },
      {
        title: '3D Digital Human Rapid Generation System',
        period: '2023/05 - Present',
        description: 'Generating a complete 3D digital human image (face, expression-driven, clothing adaptation) from a single RGB photo, supporting UE/Unity engines and Web 3D rendering.\n\nResponsibilities:\n- Researched and integrated multiple technical solutions (AI generation models, AI generation textures, Blendshape migration, facial expression driving, motion capture skeletal driving, etc.);\n- Coordinated task node plans for different technical teams and determined interface communication between technical solutions;\n- Led the testing process to ensure product stability and user experience.\n\nAchievements:\n- Achieved the industry\'s first ultra-realistic digital human generation solution in seconds;\n- Test users covered fields such as virtual human live streaming and exhibitions, and the technical solution was adopted by multiple enterprise clients.',
        achievements: 'Achieved the industry\'s first ultra-realistic digital human generation solution in seconds. Test users covered fields such as virtual human live streaming and exhibitions, and the technical solution was adopted by multiple enterprise clients.'
      },
      {
        title: 'AI Object Recognition Touch Table',
        period: '2019/03 – 2021',
        description: 'A touch table product that integrates AI object recognition technology, which can recognize objects on the desktop and display content through touch interaction.\n\nResponsibilities:\n- Designed the hardware architecture (NUC computing unit + hidden camera) and optimized AI inference performance;\n- Formulated GTM strategies and promoted the product through exhibitions and information flow advertising.\n\nAchievements:\n- Launched at the 2019 Chongqing Smart Expo and sold nearly a hundred units, with annual sales of nearly ten million yuan;\n- Reported by 36Kr, GeekPark and other media, and the technical solution was repurchased by many well-known companies such as Intel.',
        achievements: 'Launched at the 2019 Chongqing Smart Expo and sold nearly a hundred units, with annual sales of nearly ten million yuan. Reported by 36Kr, GeekPark and other media, and the technical solution was repurchased by many well-known companies such as Intel.'
      },
      {
        title: 'Overseas Crowdfunding Project (Home Smart Touch Table)',
        period: '2021/01 - 2021/05',
        description: 'Launched a 2C smart touch table through Kickstarter crowdfunding, targeting the home Party scene (object recognition, wireless charging).\n\nResponsibilities:\n- Defined product features and hardware architecture (Amlogic S905 chip + self-developed AI acceleration technology);\n- Planned preheating activities (Youtube/Facebook ads) and studied platform rules.\n\nAchievements:\n- The project ranked in the top three in the technology category and first in the hardware category, attracting 690+ supporters;\n- Terminated due to the epidemic, but accumulated a complete crowdfunding methodology and user community resources.',
        achievements: 'The project ranked in the top three in the technology category and first in the hardware category, attracting 690+ supporters. Terminated due to the epidemic, but accumulated a complete crowdfunding methodology and user community resources.'
      },
      {
        title: 'Outdoor AI Recognition Touch Window',
        period: '2018/04 – 2020/04',
        description: 'An outdoor window touch interaction achieved through AI recognition technology, without the need for outdoor wiring or store decoration changes.\n\nResponsibilities:\n- Proposed the product concept and led the design, coordinating the R&D team to realize AI recognition and touch interaction functions;\n- Responsible for dealer training and technical support after product launch.\n\nAchievements:\n- The product was adopted by multiple well-known brand stores such as Tmall, Nike (USA), and Samsung (Europe);\n- The technical solution enhanced the interactive experience of the store and received high recognition from customers.',
        achievements: 'The product was adopted by multiple well-known brand stores such as Tmall, Nike (USA), and Samsung (Europe). The technical solution enhanced the interactive experience of the store and received high recognition from customers.'
      },
      {
        title: 'Touch All-in-One Overseas E-commerce Project',
        period: '2015/01 - 2020/05',
        description: 'Established e-commerce sales channels for company hardware products through independent e-commerce platforms (Magento/Shopify) for touch all-in-one products.\n\nResponsibilities:\n- Proposed product concepts and led design, coordinating R&D teams to implement AI recognition and touch interaction features;\n- Responsible for dealer training and technical support after product launch.\n\nAchievements:\n- Products adopted by multiple well-known brand stores including Tmall, Nike (USA), Samsung (Europe);\n- Technical solutions enhanced store interactive experience and received high customer recognition.',
        achievements: 'Products adopted by multiple well-known brand stores including Tmall, Nike (USA), Samsung (Europe). Technical solutions enhanced store interactive experience and received high customer recognition.'
      }
    ],
    education: [
      { school: 'Shanghai Maritime University', degree: 'Master of International Law', period: '2007 - 2009' },
      { school: 'Lanzhou University of Finance and Economics', degree: 'Bachelor of International Trade', period: '2002 - 2006' }
    ]
  }
};

const App = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [scrollY, setScrollY] = useState(0);
  const [language, setLanguage] = useState(() => {
    // 从localStorage获取语言设置，如果没有则根据浏览器语言自动设置
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) return savedLanguage;
    
    // 根据浏览器语言自动设置默认语言
    const browserLanguage = navigator.language || navigator.languages[0];
    return browserLanguage.toLowerCase().includes('zh') ? 'zh' : 'en';
  }); // 'zh' for Chinese, 'en' for English
  const [selectedProject, setSelectedProject] = useState(null); // For project popup
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false); // For project popup
  
  // Access Code State and Logic
  const [accessCode, setAccessCode] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // 从localStorage获取认证状态
    return localStorage.getItem('isAuthenticated') === 'true';
  });
  const [showAccessForm, setShowAccessForm] = useState(() => {
    // 从localStorage获取访问表单显示状态
    return localStorage.getItem('showAccessForm') !== 'false';
  });

  const handleAccessCodeSubmit = (e) => {
    e.preventDefault();
    // Default access code validation
    if (accessCode === '13564157153') {
      setIsAuthenticated(true);
      setShowAccessForm(false);
      // 将认证状态保存到localStorage
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('showAccessForm', 'false');
    } else {
      alert(language === 'zh' ? '访问码错误，请重试。' : 'Incorrect access code, please try again.');
    }
  };

  // Handle scroll events for navbar styling and active section detection
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      // Determine active section based on scroll position
      const sections = ['home', 'about', 'skills', 'experience', 'projects', 'education'];
      const currentSection = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (currentSection) setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection Observer for fade-in animations
  const { ref: aboutRef, inView: aboutInView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const { ref: skillsRef, inView: skillsInView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const { ref: experienceRef, inView: experienceInView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const { ref: projectsRef, inView: projectsInView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const { ref: educationRef, inView: educationInView } = useInView({ triggerOnce: true, threshold: 0.1 });

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
  };

  const float = {
    hidden: { y: 0 },
    visible: { y: [-10, 10, -10], transition: { duration: 6, repeat: Infinity, ease: 'easeInOut' } }
  };

  // Project popup functions
  const openProjectModal = (project) => {
    setSelectedProject(project);
    setIsProjectModalOpen(true);
  };

  const closeProjectModal = () => {
    setIsProjectModalOpen(false);
    setSelectedProject(null);
  };

  // Get current language data
  const data = resumeData[language];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-gray-950 text-gray-100 overflow-x-hidden">
      {/* Access Code Form */}
      {showAccessForm && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-90 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-8 rounded-lg shadow-2xl max-w-md w-full mx-4 border border-gray-700">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">{language === 'zh' ? '访问验证' : 'Access Verification'}</h2>
            <p className="text-gray-300 mb-6 text-center">{language === 'zh' ? '请输入访问码以查看简历内容' : 'Please enter the access code to view the resume content'}</p>
            <p className="text-gray-400 text-sm mb-6 text-center">{language === 'zh' ? '访问码是他的手机号' : 'The access code is his mobile phone number'}</p>
            <form onSubmit={handleAccessCodeSubmit}>
              <div className="mb-4">
                <input
                  type="password"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={language === 'zh' ? '输入访问码' : 'Enter Access Code'}
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg transition duration-300"
              >
                {language === 'zh' ? '验证访问' : 'Verify Access'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Main Content */}
      {!showAccessForm && (
        <div>
          {/* Animated Background Elements */}
          <div className="fixed inset-0 -z-10 overflow-hidden">
            <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
            <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-purple-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
            <div className="absolute bottom-1/4 right-1/3 w-80 h-80 bg-pink-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
          </div>

          {/* Navigation */}
          <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrollY > 50 ? 'bg-gray-900/90 backdrop-blur-md py-3 shadow-lg' : 'bg-transparent py-5'}`}>
            <div className="container mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between">
                <a href="#home" className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">
                  {data.name}
                </a>

                {/* Desktop Navigation */}
                <nav className="hidden md:flex space-x-8">
                  {['home', 'about', 'skills', 'experience', 'projects', 'education'].map((item) => (
                    <a
                      key={item}
                      href={`#${item}`}
                      className={`text-sm font-medium transition-colors hover:text-blue-400 ${activeSection === item ? 'text-blue-400' : 'text-gray-300'}`}
                    >
                      {item === 'home' && (language === 'zh' ? '首页' : 'Home')}
                      {item === 'about' && (language === 'zh' ? '关于' : 'About')}
                      {item === 'skills' && (language === 'zh' ? '技能' : 'Skills')}
                      {item === 'experience' && (language === 'zh' ? '工作经历' : 'Experience')}
                      {item === 'projects' && (language === 'zh' ? '项目经验' : 'Projects')}
                      {item === 'education' && (language === 'zh' ? '教育背景' : 'Education')}
                    </a>
                  ))}
                </nav>

                {/* Mobile Menu Button */}
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => {
                      const newLanguage = language === 'zh' ? 'en' : 'zh';
                      setLanguage(newLanguage);
                      // 将语言设置保存到localStorage
                      localStorage.setItem('language', newLanguage);
                    }}
                    className="px-3 py-1 text-sm font-medium text-gray-300 hover:text-white rounded-md bg-gray-800 hover:bg-gray-700 transition-colors duration-200"
                  >
                    {language === 'zh' ? 'EN' : '中文'}
                  </button>
                  <div className="md:hidden">
                    <button
                      type="button"
                      className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-800 focus:outline-none"
                      aria-expanded={mobileMenuOpen}
                      onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    >
                      {mobileMenuOpen ? (
                        <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                      ) : (
                        <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Navigation */}
            <div className={`${mobileMenuOpen ? 'block' : 'hidden'} md:hidden`}>
              <div className="pt-2 pb-3 space-y-1 bg-gray-900/95 backdrop-blur-md shadow-lg">
                {['home', 'about', 'skills', 'experience', 'projects', 'education'].map((item) => (
                  <a
                    key={item}
                    href={`#${item}`}
                    className={`block px-3 py-2 rounded-md text-base font-medium ${activeSection === item ? 'bg-gray-800 text-blue-400' : 'text-gray-300 hover:bg-gray-800 hover:text-white'}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item === 'home' && (language === 'zh' ? '首页' : 'Home')}
                    {item === 'about' && (language === 'zh' ? '关于' : 'About')}
                    {item === 'skills' && (language === 'zh' ? '技能' : 'Skills')}
                    {item === 'experience' && (language === 'zh' ? '工作经历' : 'Experience')}
                    {item === 'projects' && (language === 'zh' ? '项目经验' : 'Projects')}
                    {item === 'education' && (language === 'zh' ? '教育背景' : 'Education')}
                  </a>
                ))}
              </div>
            </div>
          </header>

          <main className="pt-24 pb-16">
            {/* Hero Section */}
            <section id="home" className="container mx-auto px-4 md:px-6 py-16 md:py-24">
              <div className="flex flex-col md:flex-row items-center justify-between gap-12">
                <div className="w-full md:w-1/2 space-y-6">
                  <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight"
                  >
                    <span className="block bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">{data.name}</span>
                    <span className="block text-gray-300 mt-2">{data.title}</span>
                  </motion.h1>

                  <motion.p
                    initial={{ opacity: 0, y: 20, transition: { delay: 0.2 } }}
                    animate={{ opacity: 1, y: 0, transition: { duration: 0.6, delay: 0.2 } }}
                    className="text-lg md:text-xl text-gray-400 max-w-lg"
                  >
                    {data.tagline}
                  </motion.p>

                  <motion.div
                    initial={{ opacity: 0, y: 20, transition: { delay: 0.4 } }}
                    animate={{ opacity: 1, y: 0, transition: { duration: 0.6, delay: 0.4 } }}
                    className="flex flex-wrap gap-4 pt-4"
                  >
                    <a
                      href={`mailto:${data.contact.email}`}
                      className="inline-flex items-center px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-blue-600/20"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                      </svg>
                      {language === 'zh' ? '联系我' : 'Contact Me'}
                    </a>
                    <a
                      href={data.contact.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-5 py-3 bg-gray-800 hover:bg-gray-700 text-white font-medium rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
                    >
                      {data.contact.websiteLabel}
                      <ArrowUpRightIcon className="w-4 h-4 ml-2" />
                    </a>
                  </motion.div>
                </div>

                <motion.div
                  variants={float}
                  initial="hidden"
                  animate="visible"
                  className="w-full md:w-2/5 lg:w-1/3"
                >
                  <div className="relative aspect-square max-w-md mx-auto">
                    <div className="absolute inset-0 -m-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl blur-sm opacity-10 animate-pulse"></div>
                    <div className="relative aspect-square bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl overflow-hidden border border-gray-800 shadow-2xl">
                      <div className="absolute inset-0 bg-[url('./1.png')] bg-cover bg-center opacity-100"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-gradient-bottom">
                        <div className="flex justify-between items-center mb-4">
                          <div className="bg-blue-600/90 text-white text-xs font-bold px-3 py-1 rounded-full backdrop-blur-sm">
                            {language === 'zh' ? '技术专家' : 'Technical Expert'}
                          </div>
                          <div className="bg-purple-600/90 text-white text-xs font-bold px-3 py-1 rounded-full backdrop-blur-sm">
                            {language === 'zh' ? '产品负责人' : 'Product Owner'}
                          </div>
                        </div>
                        <h3 className="text-xl md:text-2xl font-bold text-white mb-1">{language === 'zh' ? '16年行业经验' : '16 Years of Industry Experience'}</h3>
                        <p className="text-gray-300 text-sm md:text-base">{language === 'zh' ? '主导10+款智能硬件产品商业化落地' : 'Led the commercialization of 10+ smart hardware products'}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </section>

            {/* About Section */}
            <section id="about" ref={aboutRef} className="container mx-auto px-4 md:px-6 py-16 md:py-24">
              <div className="max-w-3xl mx-auto">
                <motion.h2
                  variants={fadeInUp}
                  initial="hidden"
                  animate={aboutInView ? "visible" : "hidden"}
                  className="text-3xl md:text-4xl font-bold mb-10 text-center"
                >
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">{language === 'zh' ? '职业摘要' : 'Professional Summary'}</span>
                </motion.h2>

                <motion.div
                  variants={fadeInUp}
                  initial="hidden"
                  animate={aboutInView ? "visible" : "hidden"}
                  transition={{ delay: 0.1 }}
                  className="space-y-6 text-gray-300"
                >
                  {data.summary.map((paragraph, index) => (
                    <p key={index} className="text-lg leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </motion.div>

                <motion.div
                  variants={fadeInUp}
                  initial="hidden"
                  animate={aboutInView ? "visible" : "hidden"}
                  transition={{ delay: 0.3 }}
                  className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6"
                >
                  <div className="bg-gray-800/50 backdrop-blur-sm p-6 rounded-xl border border-gray-700/50 hover:border-blue-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/10">
                    <div className="text-3xl font-bold text-blue-400 mb-2">16+</div>
                    <div className="text-gray-400">{language === 'zh' ? '行业经验' : 'Years of Experience'}</div>
                  </div>
                  <div className="bg-gray-800/50 backdrop-blur-sm p-6 rounded-xl border border-gray-700/50 hover:border-purple-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/10">
                    <div className="text-3xl font-bold text-purple-400 mb-2">10+</div>
                    <div className="text-gray-400">{language === 'zh' ? '产品落地' : 'Product Launches'}</div>
                  </div>
                  <div className="bg-gray-800/50 backdrop-blur-sm p-6 rounded-xl border border-gray-700/50 hover:border-pink-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-pink-500/10">
                    <div className="text-3xl font-bold text-pink-400 mb-2">{language === 'zh' ? '300万+' : '300+ Million'}</div>
                    <div className="text-gray-400">{language === 'zh' ? '年营收' : 'Yearly Revenue (USD)'}</div>
                  </div>
                </motion.div>
              </div>
            </section>

            {/* Skills Section */}
            <section id="skills" ref={skillsRef} className="container mx-auto px-4 md:px-6 py-16 md:py-24">
              <motion.h2
                variants={fadeInUp}
                initial="hidden"
                animate={skillsInView ? "visible" : "hidden"}
                className="text-3xl md:text-4xl font-bold mb-16 text-center"
              >
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">{language === 'zh' ? '核心技能' : 'Core Skills'}</span>
              </motion.h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                {data.skills.map((skill, index) => (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    initial="hidden"
                    animate={skillsInView ? "visible" : "hidden"}
                    transition={{ delay: index * 0.1 }}
                    className="bg-gray-800/30 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/30 hover:border-blue-500/30 hover:shadow-xl hover:shadow-blue-500/5 transition-all duration-500"
                  >
                    <h3 className="text-xl font-bold text-blue-400 mb-4 pb-2 border-b border-gray-700/50">{skill.category}</h3>
                    <ul className="space-y-3">
                      {skill.items.map((item, i) => (
                        <li key={i} className="flex items-start">
                          <div className="flex-shrink-0 h-5 w-5 mt-0.5 rounded-full bg-blue-600/20 flex items-center justify-center">
                            <div className="h-2 w-2 rounded-full bg-blue-400"></div>
                          </div>
                          <span className="ml-3 text-gray-300">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </section>

            {/* Experience Section - New Design */}
            <section id="experience" ref={experienceRef} className="container mx-auto px-4 md:px-6 py-16 md:py-24">
              <motion.h2
                variants={fadeInUp}
                initial="hidden"
                animate={experienceInView ? "visible" : "hidden"}
                className="text-3xl md:text-4xl font-bold mb-16 text-center"
              >
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">{language === 'zh' ? '工作经历' : 'Work Experience'}</span>
              </motion.h2>

              <div className="max-w-6xl mx-auto">
                {data.experience.map((exp, index) => (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    initial="hidden"
                    animate={experienceInView ? "visible" : "hidden"}
                    transition={{ delay: index * 0.2 }}
                    className="mb-20"
                  >
                    {/* 主卡片 - 公司信息 */}
                    <div className="relative">
                      <div className="experience-main-card bg-gradient-to-br from-gray-900/90 via-gray-800/90 to-gray-900/90 backdrop-blur-xl rounded-2xl p-8 md:p-10 border border-gray-700/30 hover:border-blue-500/30 transition-all duration-500 hover:shadow-2xl hover:shadow-blue-500/10 mb-12">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
                          <div className="mb-4 lg:mb-0">
                            <h3 className="text-3xl md:text-4xl font-bold text-white mb-2 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                              {exp.position}
                            </h3>
                            <p className="text-xl text-blue-400 font-medium">{exp.company}</p>
                          </div>
                          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                            <span className="experience-period-enhanced inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-blue-300 text-sm font-medium rounded-full border border-blue-500/30 backdrop-blur-sm">
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              {exp.period}
                            </span>
                            <div className="flex gap-2">
                              <span className="px-3 py-1 bg-green-600/20 text-green-300 text-xs font-medium rounded-full border border-green-500/30">
                                {language === 'zh' ? '在职' : 'Active'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <p className="text-gray-300 text-lg leading-relaxed">{exp.description}</p>
                      </div>

                      {/* 时间轴 - 重新设计 */}
                      <div className="experience-timeline-enhanced relative pl-8 md:pl-12">
                        <div className="absolute left-0 md:left-2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 via-purple-500 to-pink-500 opacity-60"></div>
                        
                        {/* 产品管理与技术整合 */}
                        {exp.responsibilities.productManagement && (
                          <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={experienceInView ? { opacity: 1, x: 0 } : {}}
                            transition={{ delay: 0.2 }}
                            className="timeline-item-enhanced relative mb-8"
                          >
                            <div className="absolute -left-8 md:-left-10 top-1 w-4 h-4 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 border-2 border-gray-800 shadow-lg shadow-blue-500/30"></div>
                            <div className="absolute -left-6 md:-left-8 top-0 w-8 h-8 rounded-full bg-blue-500/20 blur-md"></div>
                            
                            <div className="bg-gradient-to-br from-blue-900/40 to-cyan-900/40 backdrop-blur-xl rounded-xl p-6 md:p-8 border border-blue-700/40">
                              <h4 className="text-xl md:text-2xl font-bold text-white mb-4 bg-gradient-to-r from-blue-300 to-cyan-300 bg-clip-text text-transparent">
                                {language === 'zh' ? '产品管理与技术整合' : 'Product Management & Technical Integration'}
                              </h4>
                              {exp.responsibilities.productManagement.title && (
                                <p className="text-lg text-blue-300 font-medium mb-3">{exp.responsibilities.productManagement.title}</p>
                              )}
                              {exp.responsibilities.productManagement.period && (
                                <span className="inline-block px-3 py-1 bg-blue-600/20 text-blue-200 text-sm font-medium rounded-full mb-4">
                                  {exp.responsibilities.productManagement.period}
                                </span>
                              )}
                              <div className="space-y-3 mb-4">
                                {exp.responsibilities.productManagement.responsibilities.map((resp, idx) => (
                                  <div key={idx} className="flex items-start space-x-3">
                                    <div className="w-2 h-2 rounded-full bg-blue-400 mt-2 flex-shrink-0"></div>
                                    <p className="text-gray-300 leading-relaxed">
                                      {resp}
                                    </p>
                                  </div>
                                ))}
                              </div>
                              {exp.responsibilities.productManagement.achievements && (
                                <div className="space-y-3">
                                  {exp.responsibilities.productManagement.achievements.map((achievement, idx) => (
                                    <div key={idx} className="bg-gradient-to-r from-blue-800/30 to-cyan-800/30 rounded-lg p-3 border border-blue-600/30">
                                      <p className="text-gray-200 text-sm font-medium">{achievement}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}

                        {/* 跨境业务与市场拓展 */}
                        {exp.responsibilities.crossBorderBusiness && (
                          <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={experienceInView ? { opacity: 1, x: 0 } : {}}
                            transition={{ delay: 0.3 }}
                            className="timeline-item-enhanced relative mb-8"
                          >
                            <div className="absolute -left-8 md:-left-10 top-1 w-4 h-4 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 border-2 border-gray-800 shadow-lg shadow-purple-500/30"></div>
                            <div className="absolute -left-6 md:-left-8 top-0 w-8 h-8 rounded-full bg-purple-500/20 blur-md"></div>
                            
                            <div className="bg-gradient-to-br from-purple-900/40 to-pink-900/40 backdrop-blur-xl rounded-xl p-6 md:p-8 border border-purple-700/40">
                              <h4 className="text-xl md:text-2xl font-bold text-white mb-4 bg-gradient-to-r from-purple-300 to-pink-300 bg-clip-text text-transparent">
                                {language === 'zh' ? '跨境业务与市场拓展' : 'Cross-border Business & Market Expansion'}
                              </h4>
                              {exp.responsibilities.crossBorderBusiness.title && (
                                <p className="text-lg text-purple-300 font-medium mb-3">{exp.responsibilities.crossBorderBusiness.title}</p>
                              )}
                              {exp.responsibilities.crossBorderBusiness.period && (
                                <span className="inline-block px-3 py-1 bg-purple-600/20 text-purple-200 text-sm font-medium rounded-full mb-4">
                                  {exp.responsibilities.crossBorderBusiness.period}
                                </span>
                              )}
                              <div className="space-y-3 mb-4">
                                {exp.responsibilities.crossBorderBusiness.responsibilities.map((resp, idx) => (
                                  <div key={idx} className="flex items-start space-x-3">
                                    <div className="w-2 h-2 rounded-full bg-purple-400 mt-2 flex-shrink-0"></div>
                                    <p className="text-gray-300 leading-relaxed">
                                      {resp}
                                    </p>
                                  </div>
                                ))}
                              </div>
                              {exp.responsibilities.crossBorderBusiness.achievements && (
                                <div className="space-y-3">
                                  {exp.responsibilities.crossBorderBusiness.achievements.map((achievement, idx) => (
                                    <div key={idx} className="bg-gradient-to-r from-purple-800/30 to-pink-800/30 rounded-lg p-3 border border-purple-600/30">
                                      <p className="text-gray-200 text-sm font-medium">{achievement}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}

                        {/* 运营与体系建设 */}
                        {exp.responsibilities.operations && (
                          <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={experienceInView ? { opacity: 1, x: 0 } : {}}
                            transition={{ delay: 0.4 }}
                            className="timeline-item-enhanced relative mb-8"
                          >
                            <div className="absolute -left-8 md:-left-10 top-1 w-4 h-4 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 border-2 border-gray-800 shadow-lg shadow-green-500/30"></div>
                            <div className="absolute -left-6 md:-left-8 top-0 w-8 h-8 rounded-full bg-green-500/20 blur-md"></div>
                            
                            <div className="bg-gradient-to-br from-green-900/40 to-emerald-900/40 backdrop-blur-xl rounded-xl p-6 md:p-8 border border-green-700/40">
                              <h4 className="text-xl md:text-2xl font-bold text-white mb-4 bg-gradient-to-r from-green-300 to-emerald-300 bg-clip-text text-transparent">
                                {language === 'zh' ? '运营与体系建设' : 'Operations & System Development'}
                              </h4>
                              {exp.responsibilities.operations.title && (
                                <p className="text-lg text-green-300 font-medium mb-3">{exp.responsibilities.operations.title}</p>
                              )}
                              {exp.responsibilities.operations.period && (
                                <span className="inline-block px-3 py-1 bg-green-600/20 text-green-200 text-sm font-medium rounded-full mb-4">
                                  {exp.responsibilities.operations.period}
                                </span>
                              )}
                              <div className="space-y-3 mb-4">
                                {exp.responsibilities.operations.responsibilities.map((resp, idx) => (
                                  <div key={idx} className="flex items-start space-x-3">
                                    <div className="w-2 h-2 rounded-full bg-green-400 mt-2 flex-shrink-0"></div>
                                    <p className="text-gray-300 leading-relaxed">
                                      {resp}
                                    </p>
                                  </div>
                                ))}
                              </div>
                              {exp.responsibilities.operations.achievements && (
                                <div className="space-y-3">
                                  {exp.responsibilities.operations.achievements.map((achievement, idx) => (
                                    <div key={idx} className="bg-gradient-to-r from-green-800/30 to-emerald-800/30 rounded-lg p-3 border border-green-600/30">
                                      <p className="text-gray-200 text-sm font-medium">{achievement}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}

                        {/* 早期经历 */}
                        {exp.responsibilities.earlyExperience && (
                          <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={experienceInView ? { opacity: 1, x: 0 } : {}}
                            transition={{ delay: 0.5 }}
                            className="timeline-item-enhanced relative mb-10 last:mb-0"
                          >
                            <div className="absolute -left-8 md:-left-10 top-1 w-4 h-4 rounded-full bg-gradient-to-r from-orange-500 to-red-500 border-2 border-gray-800 shadow-lg shadow-orange-500/30"></div>
                            <div className="absolute -left-6 md:-left-8 top-0 w-8 h-8 rounded-full bg-orange-500/20 blur-md"></div>
                            
                            <div className="bg-gradient-to-br from-orange-900/40 to-red-900/40 backdrop-blur-xl rounded-xl p-6 md:p-8 border border-orange-700/40">
                              <h4 className="text-xl md:text-2xl font-bold text-white mb-4 bg-gradient-to-r from-orange-300 to-red-300 bg-clip-text text-transparent">
                                {language === 'zh' ? '早期经历' : 'Early Career'}
                              </h4>
                              {exp.responsibilities.earlyExperience.title && (
                                <p className="text-lg text-orange-300 font-medium mb-3">{exp.responsibilities.earlyExperience.title}</p>
                              )}
                              {exp.responsibilities.earlyExperience.period && (
                                <span className="inline-block px-3 py-1 bg-orange-600/20 text-orange-200 text-sm font-medium rounded-full mb-4">
                                  {exp.responsibilities.earlyExperience.period}
                                </span>
                              )}
                              <div className="space-y-3 mb-4">
                                {exp.responsibilities.earlyExperience.responsibilities.map((resp, idx) => (
                                  <div key={idx} className="flex items-start space-x-3">
                                    <div className="w-2 h-2 rounded-full bg-orange-400 mt-2 flex-shrink-0"></div>
                                    <p className="text-gray-300 leading-relaxed">
                                      {resp}
                                    </p>
                                  </div>
                                ))}
                              </div>
                              {exp.responsibilities.earlyExperience.achievements && (
                                <div className="space-y-3">
                                  {exp.responsibilities.earlyExperience.achievements.map((achievement, idx) => (
                                    <div key={idx} className="bg-gradient-to-r from-orange-800/30 to-red-800/30 rounded-lg p-3 border border-orange-600/30">
                                      <p className="text-gray-200 text-sm font-medium">{achievement}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </section>

            {/* Projects Section */}
            <section id="projects" ref={projectsRef} className="container mx-auto px-4 md:px-6 py-16 md:py-24">
              <motion.h2
                variants={fadeInUp}
                initial="hidden"
                animate={projectsInView ? "visible" : "hidden"}
                className="text-3xl md:text-4xl font-bold mb-16 text-center"
              >
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">{language === 'zh' ? '项目经历' : 'Projects'}</span>
              </motion.h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                {data.projects.map((project, index) => (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    initial="hidden"
                    animate={projectsInView ? "visible" : "hidden"}
                    transition={{ delay: index * 0.15 }}
                    className="project-card"
                  >
                    <div className="project-card-header">
                      <div className="project-card-overlay"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="project-card-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-24 w-24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                        </div>
                      </div>
                      <div className="project-card-badge">
                        {project.period}
                      </div>
                    </div>
                    <div className="project-card-content">
                      <h3 className="project-card-title">{project.title}</h3>
                      <p className="project-card-description">{project.description}</p>
                      <div className="project-card-achievements">
                        <h4 className="text-sm font-semibold text-gray-300 mb-2">
                          {language === 'zh' ? '核心成果' : 'Key Achievements'}
                        </h4>
                        {project.achievements.split('；').map((achievement, idx) => (
                          <div key={idx} className="project-card-achievement-item">
                            {achievement.trim()}
                          </div>
                        ))}
                      </div>
                      <button 
                        onClick={() => openProjectModal(project)}
                        className="project-card-button"
                      >
                        {language === 'zh' ? '查看详情' : 'View Details'}
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </section>

            {/* Project Detail Modal */}
            <AnimatePresence>
              {isProjectModalOpen && selectedProject && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="project-modal"
                  onClick={closeProjectModal}
                >
                  <motion.div 
                    initial={{ scale: 0.9, y: 20 }}
                    animate={{ scale: 1, y: 0 }}
                    exit={{ scale: 0.9, y: 20 }}
                    className="project-modal-content"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="project-modal-header">
                      <div className="flex justify-between items-start">
                        <h3 className="project-modal-title">{selectedProject.title}</h3>
                        <button 
                          onClick={closeProjectModal}
                          className="text-gray-400 hover:text-white transition-colors duration-200"
                        >
                          <XMarkIcon className="h-6 w-6" />
                        </button>
                      </div>
                      <div className="mt-3">
                        <span className="project-modal-period">
                          {selectedProject.period}
                        </span>
                      </div>
                    </div>
                    
                    <div className="project-modal-body">
                      <div className="project-modal-section">
                        <h4 className="project-modal-section-title">
                          {language === 'zh' ? '项目描述' : 'Project Description'}
                        </h4>
                        <p className="project-modal-text">{selectedProject.description}</p>
                      </div>
                      
                      <div className="project-modal-section">
                        <h4 className="project-modal-section-title">
                          {language === 'zh' ? '主要成果' : 'Key Achievements'}
                        </h4>
                        <div className="space-y-2">
                          {selectedProject.achievements.split('；').map((achievement, idx) => (
                            <div key={idx} className="project-card-achievement-item">
                              {achievement.trim()}
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div className="flex justify-end pt-4">
                        <button
                          onClick={closeProjectModal}
                          className="project-modal-close"
                        >
                          {language === 'zh' ? '关闭' : 'Close'}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Education Section */}
            <section id="education" ref={educationRef} className="container mx-auto px-4 md:px-6 py-16 md:py-24">
              <motion.h2
                variants={fadeInUp}
                initial="hidden"
                animate={educationInView ? "visible" : "hidden"}
                className="text-3xl md:text-4xl font-bold mb-16 text-center"
              >
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">{language === 'zh' ? '教育背景' : 'Education'}</span>
              </motion.h2>

              <div className="max-w-3xl mx-auto">
                {data.education.map((edu, index) => (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    initial="hidden"
                    animate={educationInView ? "visible" : "hidden"}
                    transition={{ delay: index * 0.2 }}
                    className="bg-gray-800/30 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-gray-700/30 mb-8 hover:border-purple-500/30 hover:shadow-xl hover:shadow-purple-500/5 transition-all duration-500"
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                      <div>
                        <h3 className="text-2xl font-bold text-white">{edu.school}</h3>
                        <p className="text-purple-400">{edu.degree}</p>
                      </div>
                      <div className="mt-2 md:mt-0 bg-gray-700/50 text-gray-300 px-4 py-1 rounded-full text-sm">
                        {edu.period}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </section>
          </main>

          {/* Footer */}
          <footer className="bg-gray-900/80 backdrop-blur-md border-t border-gray-800 py-8">
            <div className="container mx-auto px-4 md:px-6">
              <div className="flex flex-col md:flex-row justify-between items-center">
                <div className="mb-6 md:mb-0">
                  <p className="text-gray-400 text-sm">© {new Date().getFullYear()} {data.name}. All rights reserved.</p>
                </div>
                <div className="flex space-x-6">
                  <a href={`mailto:${data.contact.email}`} className="text-gray-400 hover:text-blue-400 transition-colors duration-300">
                    <span className="sr-only">Email</span>
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                    </svg>
                  </a>
                  <a href={data.contact.website} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-400 transition-colors duration-300">
                    <span className="sr-only">Website</span>
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </footer>
        </div>
      )}
    </div>
  );
};

export default App;